import {
  useCallback,
  useEffect,
  type Dispatch,
  type SetStateAction,
} from "react";

const VIDEO_RE = /\.(mp4|webm|mov)(\?|$)/i;

/** Warm the browser cache for a URL (images only — Image() can't load video). */
function preloadImage(url?: string) {
  if (url && !VIDEO_RE.test(url)) {
    const img = new Image();
    img.src = url;
  }
}

/**
 * Shared lightbox navigation logic for cycling through media items.
 * Used by Gallery, FramesToVideo, and TextToVideo pages.
 */
export function useLightboxNavigation<T extends { id: string }>(
  items: T[],
  selectedItem: T | null,
  setSelectedItem: Dispatch<SetStateAction<T | null>>,
) {
  const currentMediaIndex = selectedItem
    ? items.findIndex((item) => item.id === selectedItem.id)
    : -1;

  const canNavigate = currentMediaIndex >= 0 && items.length > 1;

  // Prefetch the adjacent items (next + prev) so navigation lands instantly
  // instead of waiting on a cold download. Thumbnails warm the blur-up base;
  // the full image warms the sharp swap. Videos are skipped.
  useEffect(() => {
    if (currentMediaIndex < 0 || items.length < 2) return;
    const neighbors = [
      items[(currentMediaIndex + 1) % items.length],
      items[(currentMediaIndex - 1 + items.length) % items.length],
    ];
    for (const it of neighbors) {
      const m = it as { url?: string; image_url?: string; displayUrl?: string };
      preloadImage(m.displayUrl);
      preloadImage(m.url ?? m.image_url);
    }
  }, [currentMediaIndex, items]);

  const goToNextItem = useCallback(() => {
    if (currentMediaIndex >= 0 && items.length > 1) {
      const nextItem = items[(currentMediaIndex + 1) % items.length];
      if (nextItem) setSelectedItem(nextItem);
    }
  }, [currentMediaIndex, items, setSelectedItem]);

  const goToPrevItem = useCallback(() => {
    if (currentMediaIndex >= 0 && items.length > 1) {
      const prevItem =
        items[(currentMediaIndex - 1 + items.length) % items.length];
      if (prevItem) setSelectedItem(prevItem);
    }
  }, [currentMediaIndex, items, setSelectedItem]);

  return { currentMediaIndex, canNavigate, goToNextItem, goToPrevItem };
}
